import {useEffect, useState} from 'react';

type ViewportMeasurements = Pick<VisualViewport, 'height' | 'offsetTop'>;

export const calculateVisualViewportBottomOffset = (
  visualViewport: ViewportMeasurements | null = window.visualViewport,
  layoutViewportHeight: number = window.innerHeight,
): number => {
  if (!visualViewport) return 0;

  return Math.max(0, layoutViewportHeight - visualViewport.height - visualViewport.offsetTop);
};

/**
 * Continuously tracks how far the bottom of Safari's visual viewport sits
 * above the bottom of the layout viewport.
 *
 * This grows whenever on-screen browser UI -- the keyboard, the address bar,
 * or the bottom toolbar -- covers part of the screen, and shrinks back to 0
 * as that UI collapses. Unlike gating updates to "while an input is
 * focused", this stays in sync on every scroll and resize regardless of
 * scroll direction or focus state, which is what's needed to keep a fixed,
 * bottom-anchored control from disappearing behind Safari's toolbar when the
 * toolbar reappears on an upward scroll after the keyboard has already
 * closed (see issue #3106).
 */
export const useVisualViewportBottomOffset = (): number => {
  const [bottomOffset, setBottomOffset] = useState(calculateVisualViewportBottomOffset);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    const updateBottomOffset = () => setBottomOffset(calculateVisualViewportBottomOffset());

    updateBottomOffset();
    visualViewport.addEventListener('resize', updateBottomOffset);
    visualViewport.addEventListener('scroll', updateBottomOffset);
    window.addEventListener('resize', updateBottomOffset);

    return () => {
      visualViewport.removeEventListener('resize', updateBottomOffset);
      visualViewport.removeEventListener('scroll', updateBottomOffset);
      window.removeEventListener('resize', updateBottomOffset);
    };
  }, []);

  return bottomOffset;
};
